import { useCallback, useEffect, useRef, useState } from 'react'

import { useArabicSessionLanguage } from '@context/SessionSettingsContext'

interface HorizontalScrollControl<T extends HTMLElement> {
  containerRef: React.RefObject<T | null>
  scroll: (direction: 'left' | 'right', speed?: number) => void
  stopScrolling: () => void
  showFadeLeft: boolean
  showFadeRight: boolean
  handleScroll: () => void
  handleKeyDownScroll: (e: React.KeyboardEvent<HTMLButtonElement>) => void
  isScrollable: boolean
}

function getNormalizedScrollLeft(element: HTMLElement, isRTL: boolean): number {
  if (!isRTL) {
    return element.scrollLeft
  }

  const scrollLeft = element.scrollLeft
  const scrollWidth = element.scrollWidth
  const clientWidth = element.clientWidth

  if (scrollLeft < 0) {
    return scrollWidth + scrollLeft - clientWidth
  }

  return scrollWidth - scrollLeft - clientWidth
}

export const SCROLL_SPEED = {
  DEFAULT: 6.5,
  FAST: 10,
}

export function useHorizontalControlScroll<T extends HTMLElement>(): HorizontalScrollControl<T> {
  const containerRef = useRef<T | null>(null)
  const scrollAnimationRef = useRef<number | null>(null)
  const [showFadeLeft, setShowFadeLeft] = useState(false)
  const [showFadeRight, setShowFadeRight] = useState(false)
  const [isScrollable, setIsScrollable] = useState(false)
  const isArabic = useArabicSessionLanguage()

  const scroll = useCallback((direction: 'left' | 'right', speed = SCROLL_SPEED.DEFAULT) => {
    if (scrollAnimationRef.current) return

    const step = () => {
      if (containerRef.current) {
        containerRef.current.scrollLeft += direction === 'left' ? -speed : speed
      }
      scrollAnimationRef.current = requestAnimationFrame(step)
    }

    scrollAnimationRef.current = requestAnimationFrame(step)
  }, [])

  const stopScrolling = useCallback(() => {
    if (scrollAnimationRef.current) {
      cancelAnimationFrame(scrollAnimationRef.current)
      scrollAnimationRef.current = null
    }
  }, [])

  const handleKeyDownScroll = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault()

        const direction =
          e.key === 'ArrowLeft' ? (!isArabic ? 'left' : 'right') : !isArabic ? 'right' : 'left'

        scroll(direction, SCROLL_SPEED.FAST)
      }
    },
    [scroll, isArabic]
  )

  const updateFadeVisibility = useCallback(() => {
    const container = containerRef.current
    const pxAwayFromTheEdge = 20
    if (!container) {
      return
    }

    const scrollLeft = getNormalizedScrollLeft(container, isArabic)
    const { scrollWidth, clientWidth } = container

    const showScrollLeft = scrollLeft > pxAwayFromTheEdge
    const showScrollRight = scrollLeft + clientWidth < scrollWidth - pxAwayFromTheEdge
    setIsScrollable(showScrollLeft || showScrollRight)

    setShowFadeLeft(isArabic ? showScrollRight : showScrollLeft)
    setShowFadeRight(isArabic ? showScrollLeft : showScrollRight)
  }, [isArabic])

  useEffect(() => {
    updateFadeVisibility()
  }, [updateFadeVisibility])

  const handleScroll = () => updateFadeVisibility()

  return {
    containerRef,
    scroll,
    stopScrolling,
    showFadeLeft,
    showFadeRight,
    handleScroll,
    handleKeyDownScroll,
    isScrollable,
  }
}
